import React, { useState } from 'react';
import { X, Cookie, Settings } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { useCookies } from './CookieContext';

export function CookieConsent() {
  const { language } = useLanguage();
  const { showBanner, savePreferences } = useCookies();
  const [showSettings, setShowSettings] = useState(false);
  const [localPreferences, setLocalPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  const content = {
    banner: {
      title: {
        fr: 'Nous Respectons Votre Vie Privée',
        en: 'We Respect Your Privacy'
      },
      description: {
        fr: 'Nous utilisons des cookies pour améliorer votre expérience et nos services. Vous pouvez gérer vos préférences à tout moment.',
        en: 'We use cookies to enhance your experience and our services. You can manage your preferences at any time.'
      },
      acceptAll: {
        fr: 'Accepter Tout',
        en: 'Accept All'
      },
      rejectAll: {
        fr: 'Rejeter Tout',
        en: 'Reject All'
      },
      settings: {
        fr: 'Paramètres',
        en: 'Settings'
      }
    },
    preferences: {
      title: {
        fr: 'Préférences des Cookies',
        en: 'Cookie Preferences'
      },
      necessary: {
        title: {
          fr: 'Cookies Nécessaires',
          en: 'Necessary Cookies'
        },
        description: {
          fr: 'Ces cookies sont essentiels au fonctionnement du site.',
          en: 'These cookies are essential for the website to function.'
        }
      },
      analytics: {
        title: {
          fr: 'Cookies Analytiques',
          en: 'Analytics Cookies'
        },
        description: {
          fr: 'Nous aident à comprendre comment vous utilisez notre site.',
          en: 'Help us understand how you use our site.'
        }
      },
      marketing: {
        title: {
          fr: 'Cookies Marketing',
          en: 'Marketing Cookies'
        },
        description: {
          fr: 'Utilisés pour la publicité ciblée et le marketing.',
          en: 'Used for targeted advertising and marketing.'
        }
      },
      save: {
        fr: 'Sauvegarder les Préférences',
        en: 'Save Preferences'
      }
    }
  };

  if (!showBanner) return null;

  const handleAcceptAll = () => {
    savePreferences({
      necessary: true,
      analytics: true,
      marketing: true,
    });
  };

  const handleRejectAll = () => {
    savePreferences({
      necessary: true,
      analytics: false,
      marketing: false,
    });
  };

  const handleSavePreferences = () => {
    savePreferences(localPreferences);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-gray-900/95 backdrop-blur-sm border-t border-gray-800">
      <div className="container mx-auto px-6 py-6">
        {!showSettings ? (
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <Cookie className="w-8 h-8 text-blue-500" />
              <div>
                <h3 className="text-lg font-semibold text-white mb-1">
                  {content.banner.title[language]}
                </h3>
                <p className="text-gray-300 max-w-2xl">
                  {content.banner.description[language]}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowSettings(true)}
                className="px-4 py-2 text-gray-300 hover:text-white transition-colors duration-300 flex items-center"
              >
                <Settings className="w-4 h-4 mr-2" />
                {content.banner.settings[language]}
              </button>
              <button
                onClick={handleRejectAll}
                className="px-6 py-2 border border-gray-600 rounded-lg text-white hover:bg-gray-800 transition-colors duration-300"
              >
                {content.banner.rejectAll[language]}
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-6 py-2 bg-blue-500 rounded-lg text-white hover:bg-blue-600 transition-colors duration-300"
              >
                {content.banner.acceptAll[language]}
              </button>
            </div>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">
                {content.preferences.title[language]}
              </h3>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-400 hover:text-white transition-colors duration-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-6">
              {/* Necessary Cookies */}
              <div className="flex items-start space-x-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={localPreferences.necessary}
                      disabled
                      className="w-4 h-4 text-blue-500 border-gray-600 rounded focus:ring-blue-500"
                    />
                    <label className="text-white font-medium">
                      {content.preferences.necessary.title[language]}
                    </label>
                  </div>
                  <p className="text-gray-400 mt-1">
                    {content.preferences.necessary.description[language]}
                  </p>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-start space-x-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={localPreferences.analytics}
                      onChange={(e) => setLocalPreferences(prev => ({
                        ...prev,
                        analytics: e.target.checked
                      }))}
                      className="w-4 h-4 text-blue-500 border-gray-600 rounded focus:ring-blue-500"
                    />
                    <label className="text-white font-medium">
                      {content.preferences.analytics.title[language]}
                    </label>
                  </div>
                  <p className="text-gray-400 mt-1">
                    {content.preferences.analytics.description[language]}
                  </p>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-start space-x-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={localPreferences.marketing}
                      onChange={(e) => setLocalPreferences(prev => ({
                        ...prev,
                        marketing: e.target.checked
                      }))}
                      className="w-4 h-4 text-blue-500 border-gray-600 rounded focus:ring-blue-500"
                    />
                    <label className="text-white font-medium">
                      {content.preferences.marketing.title[language]}
                    </label>
                  </div>
                  <p className="text-gray-400 mt-1">
                    {content.preferences.marketing.description[language]}
                  </p>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <button
                  onClick={handleSavePreferences}
                  className="px-6 py-2 bg-blue-500 rounded-lg text-white hover:bg-blue-600 transition-colors duration-300"
                >
                  {content.preferences.save[language]}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}